(() => {
  'use strict';

  const _tagCache = {};
  let _running = false;
  let _cartAttributeSet = false;
  let _lastInfoShow = null;
  let _lastBlockState = null;

  // ── Session subscription ──────────────────────────────────────────────────

  const SESSION_KEY = 'dr-gift-subscribed';
  const COOKIE_KEY = 'dr_gift_received';
  const LOCAL_KEY = 'dr_gift_received';

  const isSessionSubscribed = () => sessionStorage.getItem(SESSION_KEY) === 'true';

  const markSessionSubscribed = () => sessionStorage.setItem(SESSION_KEY, 'true');

  const hasReceivedCookie = () =>
    document.cookie.split(';').some(c => c.trim().startsWith(COOKIE_KEY + '=1'));

  const hasReceivedLocal = () => localStorage.getItem(LOCAL_KEY) === '1';
  const setReceivedLocal = () => localStorage.setItem(LOCAL_KEY, '1');
  const clearReceivedCookie = () => {
    document.cookie = `${COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  };
  const clearReceivedLocal = () => localStorage.removeItem(LOCAL_KEY);

  const setReceivedCookie = () => {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${COOKIE_KEY}=1; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  };

  const setCartAttribute = async () => {
    try {
      const r = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { dr_gift_session: '1' } }),
      });
      return r.ok;
    } catch {
      return false;
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  const seedTagCache = () => {
    const el = document.getElementById('cart-product-tags');
    if (!el) return;
    try { Object.assign(_tagCache, JSON.parse(el.textContent)); } catch { /* silent */ }
  };

  const fetchMissingTags = async (items) => {
    const missing = items.filter(item => item.handle && !_tagCache[item.product_id]);
    await Promise.all(
      missing.map(item =>
        fetch(`/products/${item.handle}.js`)
          .then(r => r.json())
          .then(p => { _tagCache[item.product_id] = p.tags ?? []; })
          .catch(() => { /* do not cache — next recalculate will retry */ })
      )
    );
  };

  const cartAdd = async (variantId) => {
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return true;
    } catch (err) {
      console.error('[dr-gift] cartAdd failed:', err);
      return false;
    }
  };

  const cartChange = async (lineKey, quantity) => {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return true;
    } catch (err) {
      console.error('[dr-gift] cartChange failed:', err);
      return false;
    }
  };

  const refreshCart = async () => {
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl?.refreshCartSections) await cartItemsEl.refreshCartSections();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'dr-gift' } }));
  };

  const updateInfoText = (show) => {
    _lastInfoShow = show;
    const el = document.getElementById('dr-gift-info');
    if (el) el.style.display = show ? '' : 'none';
  };

  const updateTesterButton = (drActive, drGiftInCart) => {
    const block = drActive || drGiftInCart;
    _lastBlockState = block;
    const wasBlocked = document.documentElement.classList.contains('dr-active');
    // Set root class so tester-gift.js can guard its own updates
    document.documentElement.classList.toggle('dr-active', block);

    // Mini-cart: tester select button
    const selectBtn = document.getElementById('miniature-select-trigger');
    if (selectBtn) selectBtn.style.display = block ? 'none' : '';

    // Cart page: cm-bar (desktop + mobile — both matched)
    document.querySelectorAll('.cm-bar').forEach(el => {
      el.style.display = block ? 'none' : '';
    });

    // Sync progress bar visibility with block state, but only in tester phase —
    // shipping phase bar stays visible even with dr-gift active.
    if (block) {
      document.querySelectorAll('.cart-miniature-bar').forEach(el => {
        el.dataset.drHidden = 'true';
        if (el.dataset.phase === 'tester') el.style.display = 'none';
      });
    } else {
      document.querySelectorAll('.cart-miniature-bar').forEach(el => {
        el.dataset.drHidden = 'false';
        el.style.display = '';
      });
    }
  };

  // ── Recalculate ───────────────────────────────────────────────────────────

  const recalculate = async () => {
    if (_running) return;
    _running = true;
    let showInfo = false;
    let drActive = false;
    let drGiftInCart = false;
    let _infoResolved = false;
    let _stateResolved = false;

    try {
      const settings = window.theme?.shopSettings?.drGift;
      if (!settings?.variantId) return;

      // Eligibility: logged-in customer with tags (server-side) OR subscribed this session.
      // Session path: flag set in sessionStorage by subscription form listeners below.
      // hasReceivedGift blocks re-claims even for session-based subscribers (logged-in check).
      // Mirrored in Discount Function (dr-gift-function-rs) — both must stay in sync.
      const isLoggedIn = settings.isLoggedIn ?? false;
      const sessionSubscribed = isLoggedIn
        ? isSessionSubscribed()
        : (isSessionSubscribed() || settings.subscribed);
      const hasReceivedGift = settings.hasReceivedGift;
      const eligible = settings.customerEligible || (sessionSubscribed && !hasReceivedGift);
      const variantId = String(settings.variantId);

      let cart;
      try {
        cart = await fetch('/cart.js').then(r => r.json());
      } catch (_) {
        return;
      }

      seedTagCache(); // prefer server-rendered tags; avoids redundant network fetches
      await fetchMissingTags(cart.items);

      // If any item's tags couldn't be resolved, bail — avoid wrong cart mutations.
      // The next cart:updated event will retry with a fresh fetch.
      if (cart.items.some(item => _tagCache[item.product_id] === undefined)) return;

      const hasTravelSet = cart.items.some(item =>
        _tagCache[item.product_id]?.includes('travel-set')
      );

      const hasQualifyingFullSize = !hasTravelSet && cart.items.some(item => {
        const tags = _tagCache[item.product_id];
        return tags?.includes('full-size') &&
          !tags.includes('dr-gift') &&
          !tags.includes('tester-gift');
      });

      const drGiftItem = cart.items.find(item => String(item.variant_id) === variantId);
      drGiftInCart = Boolean(drGiftItem);

      const testerItems = cart.items.filter(item =>
        _tagCache[item.product_id]?.includes('tester-gift')
      );

      const shouldHaveGift = eligible && hasQualifyingFullSize;
      const alreadyReceived = hasReceivedGift || hasReceivedCookie() || hasReceivedLocal();
      drActive = shouldHaveGift && !alreadyReceived;
      // Show info banner only when customer is not eligible AND has never received the gift
      showInfo = !eligible && hasQualifyingFullSize && !alreadyReceived;
      _infoResolved = true;
      _stateResolved = true;

      if (drActive) {
        // Set cart attribute so Discount Function can apply 100% discount for session subscribers.
        // _cartAttributeSet guards against redundant /cart/update.js calls on every recalculate.
        if (sessionSubscribed && !settings.customerEligible && !_cartAttributeSet) {
          const attrOk = await setCartAttribute();
          if (attrOk) _cartAttributeSet = true;
        }

        // DR-gift always takes priority: remove any tester-gift lines present.
        // If removal fails, abort — don't add dr-gift while tester is still in cart.
        if (testerItems.length > 0) {
          for (const item of testerItems) {
            const ok = await cartChange(item.key, 0);
            if (!ok) {
              drActive = false;
              await refreshCart();
              return;
            }
          }
        }
        if (!drGiftInCart) {
          const added = await cartAdd(variantId);
          if (added) { setReceivedCookie(); setReceivedLocal(); drGiftInCart = true; }
          else drActive = false;
          await refreshCart();
        } else if (drGiftItem?.quantity > 1) {
          // Clamp to 1 if customer manually bumped qty
          await cartChange(drGiftItem.key, 1);
          await refreshCart();
        } else if (testerItems.length > 0) {
          // Tester removed but dr-gift was already present — still need to refresh
          await refreshCart();
        }
      } else if (drGiftInCart && (!shouldHaveGift || hasReceivedGift)) {
        await cartChange(drGiftItem.key, 0);
        drGiftInCart = false;
        clearReceivedCookie();
        clearReceivedLocal();
        await refreshCart();
      }
    } finally {
      if (_infoResolved) updateInfoText(showInfo);
      if (_stateResolved) updateTesterButton(drActive, drGiftInCart);
      _running = false;
    }
  };

  // ── Event listeners ───────────────────────────────────────────────────────

  const _debouncedRecalculate = debounce(recalculate, 800);

  document.addEventListener('cart:updated', (e) => {
    if (e.detail?.source === 'dr-gift') return;
    // Section re-renders on quantity change — immediately restore cached state
    // to prevent server HTML from flashing before recalculate runs.
    if (e.detail?.source === 'cart-items') {
      if (_lastInfoShow !== null) updateInfoText(_lastInfoShow);
      if (_lastBlockState !== null) {
        const selectBtn = document.getElementById('miniature-select-trigger');
        if (selectBtn) selectBtn.style.display = _lastBlockState ? 'none' : '';
        document.querySelectorAll('.cm-bar').forEach(el => {
          el.style.display = _lastBlockState ? 'none' : '';
        });
        document.querySelectorAll('.cart-miniature-bar').forEach(el => {
          el.dataset.drHidden = _lastBlockState ? 'true' : 'false';
          if (el.dataset.phase === 'tester') el.style.display = _lastBlockState ? 'none' : '';
        });
      }
    }
    _debouncedRecalculate();
  });
  document.addEventListener('cartdrawer:opened', () => {
    // Hide banner immediately to prevent server-rendered flash;
    // recalculate will show it again if the customer truly needs it.
    updateInfoText(_lastInfoShow === true);
    recalculate();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _debouncedRecalculate);
  } else {
    _debouncedRecalculate();
  }

  // ── Subscription form listeners ───────────────────────────────────────────

  // 1. Omnisend popup widget — official event API (api-docs.omnisend.com/reference/omnisend-forms-events)
  //    Cookie guard prevents re-claiming across sessions; sessionStorage persists within session.
  window.addEventListener('omnisendForms', (e) => {
    if (e.detail?.type === 'submit') {
      if (!hasReceivedCookie() && !hasReceivedLocal()) markSessionSubscribed();
      _debouncedRecalculate();
    }
  });

  // 2. Shopify native forms (popup section, newsletter section)
  //    Contain hidden input name="contact[tags]" with value including "email_subscribed".
  //    These POST and cause a page reload — sessionStorage persists, gift adds on reload.
  document.addEventListener('submit', (e) => {
    const tagInput = e.target.querySelector('input[name="contact[tags]"]');
    if (tagInput?.value.includes('email_subscribed') && !hasReceivedCookie() && !hasReceivedLocal()) {
      markSessionSubscribed();
    }
  });

  // 3. Footer custom Omnisend form (#CustomFooterNewsletter) — AJAX-based.
  //    Set flag optimistically on submit; delay covers the Omnisend API call duration.
  const _footerForm = document.getElementById('CustomFooterNewsletter');
  if (_footerForm) {
    _footerForm.addEventListener('submit', () => {
      if (!hasReceivedCookie() && !hasReceivedLocal()) markSessionSubscribed();
      setTimeout(_debouncedRecalculate, 1500);
    });
  }
})();
